/* CRA's ESLint loader does not ship the react-hooks/set-state-in-effect rule */
import { useEffect, useState } from "react";
import { Sliders, Save, RotateCcw, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DEFAULTS = { behavioral: 1.0, technical: 1.0, situational: 1.0 };

const ROWS = [
  {
    key: "behavioral",
    label: "Behavioral",
    helper: "Past experience, STAR-style questions",
    palette: "blue",
  },
  {
    key: "technical",
    label: "Technical",
    helper: "Concrete skill / domain depth",
    palette: "emerald",
  },
  {
    key: "situational",
    label: "Situational",
    helper: "Hypothetical scenarios, judgment calls",
    palette: "violet",
  },
];

const PALETTE_BG = {
  blue: "bg-blue-50 border-blue-200 text-blue-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  violet: "bg-violet-50 border-violet-200 text-violet-700",
};

export default function ScorecardRubric({ jobId, initialWeights }) {
  const [weights, setWeights] = useState(initialWeights || DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (initialWeights) setWeights(initialWeights);
  }, [initialWeights]);

  const update = (key, value) => {
    setWeights((w) => ({ ...w, [key]: Number(value) }));
    setDirty(true);
  };

  const reset = () => {
    setWeights(DEFAULTS);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}/scorecard`, weights);
      toast.success("Scorecard weights saved");
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="scorecard-rubric-card"
      className="mt-6 border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <Sliders className="h-3 w-3" /> Scorecard rubric — weight per category
        </div>
        {dirty && (
          <span className="text-[10px] uppercase tracking-[0.2em] text-amber-600">
            Unsaved
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Each question already carries one of three categories. Sliders re-weight
        each candidate&apos;s overall score (0–3x). Set a category to 0 to
        ignore it entirely.
      </p>

      <div className="mt-5 space-y-4">
        {ROWS.map((r) => (
          <div
            key={r.key}
            data-testid={`scorecard-row-${r.key}`}
            className="flex flex-wrap items-center gap-3"
          >
            <span
              className={`inline-flex w-32 items-center justify-center border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${PALETTE_BG[r.palette]}`}
            >
              {r.label}
            </span>
            <input
              type="range"
              data-testid={`scorecard-slider-${r.key}`}
              min={0}
              max={3}
              step={0.1}
              value={weights[r.key]}
              onChange={(e) => update(r.key, e.target.value)}
              className="flex-1 accent-blue-700"
            />
            <span
              data-testid={`scorecard-value-${r.key}`}
              className="w-12 text-right text-sm font-semibold text-slate-900"
            >
              {weights[r.key].toFixed(1)}x
            </span>
            <span className="hidden text-xs text-slate-500 lg:inline">
              {r.helper}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
        <button
          type="button"
          onClick={reset}
          data-testid="scorecard-reset-btn"
          className="rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="mr-1 inline h-3 w-3" /> Reset
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="scorecard-save-btn"
          className="rounded-sm bg-blue-700 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-600 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
          ) : (
            <Save className="mr-1 inline h-3 w-3" />
          )}
          Save weights
        </button>
      </div>
    </section>
  );
}
